import type { SpotDistanceSegment, Terrain } from '@firesim/simkit-core';
import { getTerrainWithOverrides } from '@firesim/simkit-core';

export enum TerrainCodes {
    Empty = 0,           // black hole
    GambaRestricted = 1, // gamba restricted
    Grass = 2,           // grass  
    Break = 3,           // fire break
    House = 4,           // house
    GambaGrass = 5       // gamba grass
};

export type TerrainCodeKey = keyof typeof TerrainCodes;
export type TerrainCodeValue = number;

export const terrainCodeValues: TerrainCodes[] = Object.values(TerrainCodes).filter(
  (value) => typeof value === 'number'
) as TerrainCodes[];

const spotDistanceTemplates: Record<string, Array<SpotDistanceSegment>> = {
    grass: [
    {
        weight: 0.20,
        minDistance: 1,
        maxDistance: 4
    },
    {
        // Rare extreme long-tail spots.
        weight: 0.005,
        minDistance: 25,
        maxDistance: 50
    }],
    gambaGrass: [
    {
        // Rare extreme long-range spotting — gamba's defining fire behaviour.
        weight: 0.008,
        minDistance: 60,
        maxDistance: 150
    }]
};

export const baseTerrainTypes: Record<TerrainCodes, Terrain<TerrainCodes>> = {
  [TerrainCodes.Empty]: {
    code: TerrainCodes.Empty,
    colour: {
      red: 0,
      green: 0,
      blue: 0,
    },
    fuelLoad: 0  // black hole
  },
  [TerrainCodes.GambaRestricted]: {
    code: TerrainCodes.GambaRestricted,
    colour: {
      red: 150,
      green: 180,
      blue: 90,
    },
    fuelLoad: 0.7,  // gamba restricted
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.Grass]: {
    code: TerrainCodes.Grass,
    colour: {
      red: 173,
      green: 176,
      blue: 117,
    },
    fuelLoad: 0.7,  // grass
    fuelLoadFuzzRange: 1,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.Break]: {
    code: TerrainCodes.Break,
    colour: {
      red: 155,
      green: 116,
      blue: 82,
    },
    fuelLoad: 0.2  // fire break
  },
  [TerrainCodes.House]: {
    code: TerrainCodes.House,
    colour: {
      red: 92,
      green: 65,
      blue: 50,
    },
    fuelLoad: 0.4  // house (legacy value)
  },
  [TerrainCodes.GambaGrass]: {
    code: TerrainCodes.GambaGrass,
    colour: {
      red: 99,
      green: 113,
      blue: 49,
    },
    fuelLoad: 1.1,  // gamba grass - highest fuel load
    fuelLoadFuzzRange: 0,
    spotDistanceDistribution: spotDistanceTemplates.gambaGrass
  }
};

export const terrainTypes = getTerrainWithOverrides(baseTerrainTypes);

export const terrain: (typeof terrainTypes)[TerrainCodes][] = Object.values(terrainTypes);
